import {Pipe, PipeTransform} from '@angular/core';
import {ConfigType} from 'smag-commons/cfg/connector';
import {I18NService} from '../../i18n/i18n.service';
import {isDefined} from '../../commons/commons';
import {
    DataType as ModbusDataType,
    Endianness as ModbusWordType,
    Endianness,
    FrameType as ModbusFrameType,
    RegType as ModbusRegType
} from 'smag-commons/cfg/connector/modbus';
import {DataType as IEC61850DataType, RefType as IEC61850RefType} from 'smag-commons/cfg/connector/iec61850';
import {DeviceMode as MergeDeviceMode} from 'smag-commons/cfg/connector/merge';
import {
    MeasureType,
    MeasureType as MeteoMeasureType,
    SourceType as MeteoSourceType
} from 'smag-commons/cfg/connector/meteo';
import {
    AuthType as MqttAuthType,
    Chain2GateMeasureType as MqttChain2GateMeasureType,
    DeviceType as MqttDeviceType,
    DinoPlusMeasureType as MqttDinoPlusMeasureType,
    DuelMeasureType as MqttDuelMeasureType,
    MiniGatewayMeasureType, UrbanDatasetMeasureType
} from 'smag-commons/cfg/connector/mqtt';

@Pipe({name: 'connectorType'})
export class ConnectorTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ConfigType): string {
        switch (type) {
            case ConfigType.MODBUS:
                return 'Modbus';
            case ConfigType.BACNET:
                return 'BACnet';
            case ConfigType.IEC61850:
                return 'IEC61850';
            case ConfigType.MERGE:
                return 'Merge';
            case ConfigType.METEO:
                return 'Meteo';
            default:
                return type;
        }
    }
}

@Pipe({name: 'pollingPolicy'})
export class PollingPolicyPipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    transform(device: { interval?: number, cron?: string }): string {
        if (isDefined(device.interval)) {
            if ((device.interval % 3600) === 0) {
                return this.i18n.translate('{0}h', device.interval / 3600);
            } else if ((device.interval % 60) === 0) {
                return this.i18n.translate('{0}\'', device.interval / 60);
            } else {
                return this.i18n.translate('{0}\'\'', device.interval);
            }
        } else if (device.cron) {
            return this.i18n.translate('Cron');
        } else {
            return this.i18n.translate('None');
        }
    }
}


@Pipe({name: 'modbusFrameType'})
export class ModbusFrameTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ModbusFrameType): string {
        switch (type) {
            case ModbusFrameType.SOCKET:
                return 'TCP/IP';
            case ModbusFrameType.RTU:
                return 'RTU';
            case ModbusFrameType.ASCII:
                return 'Ascii';
            case ModbusFrameType.BINARY:
                return 'Binary';
            default:
                return type;
        }
    }
}

@Pipe({name: 'modbusDataType'})
export class ModbusDataTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ModbusDataType): string {
        switch (type) {
            case ModbusDataType.FLOAT:
                return 'Float';
            case ModbusDataType.INTEGER:
                return 'Integer';
            case ModbusDataType.BOOLEAN:
                return 'Boolean';
            default:
                return type;
        }
    }
}

@Pipe({name: 'modbusRegType'})
export class ModbusRegTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ModbusRegType): string {
        switch (type) {
            case ModbusRegType.DISCRETE:
                return 'Discrete';
            case ModbusRegType.COIL:
                return 'Coil';
            case ModbusRegType.INPUT:
                return 'Input';
            case ModbusRegType.HOLDING:
                return 'Holding';
            default:
                return type;
        }
    }
}

@Pipe({name: 'modbusWordType'})
export class ModbusWordTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ModbusWordType): string {
        switch (type) {
            case Endianness.BIG:
                return 'Big-endian';
            case Endianness.LITTLE:
                return 'Little-endian';
            default:
                return type;
        }
    }
}

@Pipe({name: 'iec61850DataType'})
export class IEC61850DataTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: IEC61850DataType): string {
        switch (type) {
            case IEC61850DataType.FLOAT:
                return 'Float';
            case IEC61850DataType.INTEGER:
                return 'Integer';
            case IEC61850DataType.BOOLEAN:
                return 'Boolean';
            default:
                return type;
        }
    }
}

@Pipe({name: 'iec61850RefType'})
export class IEC61850RefTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: IEC61850RefType): string {
        switch (type) {
            case IEC61850RefType.MEASURE:
                return 'Measure';
            case IEC61850RefType.CONTROL:
                return 'Control';
            case IEC61850RefType.STATUS:
                return 'Status';
            default:
                return type;
        }
    }
}

@Pipe({name: 'mergeDeviceMode'})
export class MergeDeviceModePipe implements PipeTransform {

    constructor() {
    }

    transform(mode: MergeDeviceMode): string {
        switch (mode) {
            case MergeDeviceMode.WAIT_FOR_ALL:
                return 'Wait for all';
            case MergeDeviceMode.EARLY_EMIT:
                return 'Early emit';
            default:
                return mode;
        }
    }
}

@Pipe({name: 'meteoSourceType'})
export class MeteoSourceTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: MeteoSourceType): string {
        switch (type) {
            case MeteoSourceType.METEOMATICS:
                return 'Meteomatics';
            default:
                return type;
        }
    }
}

@Pipe({name: 'meteoMeasureType'})
export class MeteoMeasureTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: MeteoMeasureType): string {
        switch (type) {
            case MeteoMeasureType.TEMPERATURE:
                return 'Temperature';
            case MeteoMeasureType.RADIATION:
                return 'Radiation';
            case MeteoMeasureType.DEW_POINT:
                return 'Dew point';
            case MeteoMeasureType.WIND_CHILL:
                return 'Wind chill';
            case MeteoMeasureType.HEAT_INDEX:
                return 'Heat index';
            case MeteoMeasureType.PRESSURE:
                return 'Pressure';
            case MeteoMeasureType.HUMIDITY:
                return 'Humidity';
            case MeteoMeasureType.RAINFALL:
                return 'Rainfall';
            case MeteoMeasureType.UV:
                return 'UV index';
            case MeteoMeasureType.LIFTED_INDEX:
                return 'Lifted index';
            case MeteoMeasureType.CLOUD_COVERAGE:
                return 'Cloud coverage';
            case MeteoMeasureType.WIND_SPEED:
                return 'Wind speed';
            case MeteoMeasureType.WIND_DIR:
                return 'Wind direction';
            case MeteoMeasureType.WIND_GUSTS:
                return 'Wind gusts';
            case MeasureType.WEATHER_STATE:
                return 'Overall weather state';
            default:
                return type;
        }
    }
}

@Pipe({name: 'mqttAuthType'})
export class MqttAuthTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: MqttAuthType): string {
        switch (type) {
            case MqttAuthType.CREDENTIALS:
                return 'Credentials';
            case MqttAuthType.CERTIFICATES:
                return 'Certificates';
            default:
                return type;
        }
    }
}

@Pipe({name: 'mqttDeviceType'})
export class MqttDeviceTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: MqttDeviceType): string {
        switch (type) {
            case MqttDeviceType.CHAIN2GATE:
                return 'Chain2Gate Plug';
            case MqttDeviceType.DINOPLUS_MOME:
                return 'Dino+';
            case MqttDeviceType.DUEL:
                return 'DUEL';
            case MqttDeviceType.MGW:
                return 'Minigateway';
            case MqttDeviceType.URBAN_DATASET:
                return 'Urban Dataset';
            default:
                return type;
        }
    }
}

@Pipe({name: 'MqttUrbanDatasetMeasureType'})
export class MqttUrbanDatasetMeasureTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: UrbanDatasetMeasureType): string {
        switch (type) {
            case UrbanDatasetMeasureType.QUARTERLY_HOUR_ABSORBED_ENERGY:
                return 'Quarterly hour absorbed energy';
            case UrbanDatasetMeasureType.QUARTERLY_HOUR_INJECTED_ENERGY:
                return 'Quarterly hour injected energy';
            case UrbanDatasetMeasureType.TOTAL_INJECTED_ENERGY:
                return 'Total injected energy';
            case UrbanDatasetMeasureType.TOTAL_ABSORBED_ENERGY:
                return 'Total absorbed energy';
            default:
                return type;
        }
    }
}

@Pipe({name: 'mqttMiniGatewayMeasureType'})
export class MqttMiniGatewayMeasureTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: MiniGatewayMeasureType): string {
        switch (type) {
            case MiniGatewayMeasureType.QUARTERLY_HOUR_ABSORBED_ENERGY:
                return 'Quarterly hour absorbed energy';
            case MiniGatewayMeasureType.QUARTERLY_HOUR_INJECTED_ENERGY:
                return 'Quarterly hour injected energy';
            case MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY:
                return 'Quarterly hour produced energy';
            case MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_2:
                return 'Quarterly hour produced energy (2)';
            case MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_3:
                return 'Quarterly hour produced energy (3)';
            case MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_4:
                return 'Quarterly hour produced energy (4)';
            case MiniGatewayMeasureType.TOTAL_INJECTED_ENERGY:
                return 'Total injected energy';
            case MiniGatewayMeasureType.TOTAL_ABSORBED_ENERGY:
                return 'Total absorbed energy';
            case MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_INJECTED_POWER:
                return 'Quarterly hour average injected power';
            case MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_ABSORBED_POWER:
                return 'Quarterly hour average absorbed power';
            case MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER:
                return 'Quarterly hour average produced power';
            case MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_2:
                return 'Quarterly hour average produced power (2)';
            case MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_3:
                return 'Quarterly hour average produced power (3)';
            case MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_4:
                return 'Quarterly hour average produced power (4)';
            default:
                return type;
        }
    }
}

@Pipe({name: 'mqttChain2GateMeasureType'})
export class MqttChain2GateMeasureTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: MqttChain2GateMeasureType): string {
        switch (type) {
            case MqttChain2GateMeasureType.INSTANT_ABSORBED_POWER:
                return 'Instant absorbed power';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_AVERAGE_ABSORBED_POWER:
                return 'Quarterly hour average absorbed power';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_ABSORBED_ENERGY:
                return 'Quarterly hour absorbed energy';
            case MqttChain2GateMeasureType.INSTANT_INJECTED_POWER:
                return 'Instant injected power';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_AVERAGE_INJECTED_POWER:
                return 'Quarterly hour average injected power';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_INJECTED_ENERGY:
                return 'Quarterly hour injected energy';
            case MqttChain2GateMeasureType.INSTANT_PRODUCED_POWER:
                return 'Instant produced power';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER:
                return 'Quarterly hour average produced power';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY:
                return 'Quarterly hour produced energy';
            case MqttChain2GateMeasureType.INSTANT_PRODUCED_POWER_2:
                return 'Instant produced power (2)';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_2:
                return 'Quarterly hour average produced power (2)';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_2:
                return 'Quarterly hour produced energy (2)';
            case MqttChain2GateMeasureType.INSTANT_PRODUCED_POWER_3:
                return 'Instant produced power (3)';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_3:
                return 'Quarterly hour average produced power (3)';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_3:
                return 'Quarterly hour produced energy (3)';
            case MqttChain2GateMeasureType.INSTANT_PRODUCED_POWER_4:
                return 'Instant produced power (4)';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_4:
                return 'Quarterly hour average produced power (4)';
            case MqttChain2GateMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_4:
                return 'Quarterly hour produced energy (4)';
            default:
                return type;
        }
    }
}

@Pipe({name: 'mqttDinoPlusMeasureType'})
export class MqttDinoPlusMeasureTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: MqttDinoPlusMeasureType): string {
        switch (type) {
            case MqttDinoPlusMeasureType.TOTAL_ABSORBED_ENERGY:
                return 'Total absorbed energy';
            case MqttDinoPlusMeasureType.TOTAL_INJECTED_ENERGY:
                return 'Total injected energy';
            case MqttDinoPlusMeasureType.TOTAL_PRODUCED_ENERGY:
                return 'Total produced energy';
            case MqttDinoPlusMeasureType.AVERAGE_ABSORBED_POWER:
                return 'Average absorbed power';
            case MqttDinoPlusMeasureType.AVERAGE_INJECTED_POWER:
                return 'Average injected power';
            case MqttDinoPlusMeasureType.AVERAGE_PRODUCED_POWER:
                return 'Average produced power';
            case MqttDinoPlusMeasureType.ABSORBED_ENERGY:
                return 'Absorbed energy';
            case MqttDinoPlusMeasureType.INJECTED_ENERGY:
                return 'Injected energy';
            case MqttDinoPlusMeasureType.PRODUCED_ENERGY:
                return 'Produced energy';
            default:
                return type;
        }
    }
}

@Pipe({name: 'mqttDuelMeasureType'})
export class MqttDuelMeasureTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: MqttDuelMeasureType): string {
        switch (type) {
            case MqttDuelMeasureType.INSTANT_ABSORBED_POWER:
                return 'Instant absorbed power';
            case MqttDuelMeasureType.QUARTERLY_HOUR_AVERAGE_ABSORBED_POWER:
                return 'Quarterly hour average absorbed power';
            case MqttDuelMeasureType.QUARTERLY_HOUR_ABSORBED_ENERGY:
                return 'Quarterly hour absorbed energy';
            case MqttDuelMeasureType.INSTANT_INJECTED_POWER:
                return 'Instant injected power';
            case MqttDuelMeasureType.QUARTERLY_HOUR_AVERAGE_INJECTED_POWER:
                return 'Quarterly hour average injected power';
            case MqttDuelMeasureType.QUARTERLY_HOUR_INJECTED_ENERGY:
                return 'Quarterly hour injected energy';
            case MqttDuelMeasureType.INSTANT_PRODUCED_POWER:
                return 'Instant produced power';
            case MqttDuelMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER:
                return 'Quarterly hour average produced power';
            case MqttDuelMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY:
                return 'Quarterly hour produced energy';
            default:
                return type;
        }
    }
}
